import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, notification, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Modal, Search, Button, DataGrid } from '@hydrodigital/design-system';
import useDebounce from '@/hooks/useDebounce';
import './index.scss';
import HistoricalService from '../../domain/Historical/HistoricalService';

const historicalService = new HistoricalService();

const HistoricalTabla: FC<{
	isCurrentTab: boolean;
}> = ({ isCurrentTab = false }) => {
	const navigate = useNavigate();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const debouncedSearch = useDebounce(searchValue, 500);
	const [fileSelect, setFileSelect] = useState<any | null>(null);
	const [historicalFiles, setHistoricalFiles] = useState<any>([]);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const intervalRef = useRef<any>(null);

	const handleUpdateHistoricalFiles = useCallback(
		async (loading = true) => {
			const res = await historicalService.getHistoricalFiles({
				name: debouncedSearch || null,
				status: null,
				page: paginationState.pageIndex + 1,
				size: paginationState.pageSize,
				loading,
			});
			setHistoricalFiles(res.data);
			setTotalRows(res.totalRecords);
		},
		[paginationState, debouncedSearch],
	);

	  const gridColumns = [
        {
            header: 'File Name',
            accessorKey: 'name',
            cell: ({ row }: any) => (
                <>
                    <span className="table-data-gis">{row.original?.name}</span>
                </>
            ),
        },
        {
            header: 'Upload Status',
            accessorKey: 'uploadStatus',
            cell: ({ row }: any) => {
                const status = row.original?.status;
                const fontColor = '#232B36';
                let color = '#FFEECB';
                if (status === 'COMPLETED') color = '#C0EFDE';
                if (status === 'IN PROGRESS') color = '#FFEECB';
                if (status === 'FAILED') color = '#FFD3D3';
                return (
                    status && (
                        <Tag color={color} style={{ color: fontColor }}>
                            {status}
                        </Tag>
                    )
                );
            },
        },
        {
            header: 'Upload Status Message',
            accessorKey: 'uploadStatusMessage',
            cell: ({ row }: any) => <span className="table-data-gis">{row.original?.statusMessage}</span>,
        },
        {
            header: 'Actions',
            accessorKey: 'uploadId',
            cell: ({ row }: any) => {
                const { name, id } = row.original || {};
                return (
                    <Tooltip title="Delete">
                        <DeleteOutlined
                            className="mr-1"
                            style={{ fontSize: '1.5rem' }}
                            onClick={() => {
                                setFileSelect({ label: name, id });
                                setShowDeleteModal(true);
                            }}
                            rev={undefined}
                        />
                    </Tooltip>
                );
            },
        },
    ];

	useEffect(() => {
		handleUpdateHistoricalFiles();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedSearch]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedSearch]);

	useEffect(() => {
		if (isCurrentTab) {
			intervalRef.current = setInterval(() => {
				handleUpdateHistoricalFiles(false);
			}, 5000);
		}

		return () => {
			clearInterval(intervalRef.current);
		};
	}, [isCurrentTab, paginationState.pageIndex, paginationState.pageSize, debouncedSearch]);

	return (
		<div>
			<div className="flex justify-between">
				<div>
					<Search
						size="small"
						placeholder="Search"
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						onClear={() => {
							setSearchValue('');
						}}
						showClearButton
					/>
				</div>

				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Upload HistoricalFile"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`/upload/historicalfiles`);
						}}
					/>
				</div>
			</div>
			<DataGrid
					className="mt-3"
					data={historicalFiles}
					columns={gridColumns}
					pagination={{
						enabled: true,
						manualPagination: true,
						rowCount: totalRows,
						defaultPageSize: 10,
						pageSizeOptions: [10, 20, 50],
					}}
					stickyHeader={true}
					maxHeight="calc(100vh - 320px)"
					paginationState={paginationState}
					onPaginationChange={setPaginationState}
				/>
			<Modal
				isOpen={showDeleteModal}
				size="s"
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={true}
				onClose={() => setShowDeleteModal(false)}
			>
				<Modal.Header>
					<div className="flex column items-center">
						<span
							className="mb-2"
							style={{
								color: '#232B36',
								fontSize: '1.2rem',
								fontWeight: 'bold',
							}}
						>
							Historical File delete?
						</span>
					</div>
				</Modal.Header>

				<Modal.Content>
					<div className="flex column items-center">
						<span
							className="mb-3"
							style={{
								color: '#74788D',
								fontSize: '1rem',
								fontWeight: '600',
								textAlign: 'center',
							}}
						>
							Are you sure to delete {fileSelect?.label} ?
						</span>
					</div>
				</Modal.Content>

				<div className="separator mb-2 mt-2" />

				<Modal.Footer>
					<div className="flex">
						<div className="mr-3">
							<Button
								label="No"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
								onClick={() => setShowDeleteModal(false)}
							/>
						</div>

						<Button
							label="Yes"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
							}}
							onClick={async () => {
								console.log('paramSelect', fileSelect);
								if (fileSelect && fileSelect.id) {
									await historicalService.deleteHistoricalFile(fileSelect?.id?.toString());
									notification.success({
										message: 'Successful deletion',
										description: 'File deleted successfully',
										placement: 'topRight',
									});
									setShowDeleteModal(false);
									handleUpdateHistoricalFiles();
								}
							}}
						/>
					</div>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default HistoricalTabla;
